/**
 * WO MAN IST, STEHT IN DER ADRESSE (23.09.2026, .217).
 *
 * ORIGINALTEXT MARVIN
 *   „auf der mobilen version auf dem handy im brauchser passiert folgendes:
 *    ich bin mit dem browser auf dem wochenplan, dann aktualisiere ich den
 *    browser und dann springt er nach dem reload auf die kategorie
 *    "aufträge". bitte mal gegenprüfen, dass wird ja überall sein
 *    vermutlich."
 *
 * ER HAT RECHT, UND ES WAR ÜBERALL SO. Der Bildschirm stand allein im Zustand
 * der Seite (`useState('auftraege')`), und ein Neuladen setzt den Zustand
 * zurück. Das Telefon lädt dazu von selbst neu, wenn der Browser im
 * Hintergrund war.
 *
 * JETZT STEHT ER HINTER DEM # DER ADRESSE: #/wochenplan, #/akte/<id>/<reiter>.
 * Der Teil hinter # geht nie an den Server, also braucht er keinen eigenen
 * Weg dort. Geschrieben wird mit `replaceState`: kein neuer Eintrag im
 * Verlauf je Klick, sonst müsste man sich mit „zurück" durch jeden Menüpunkt
 * klicken, den man je angesehen hat.
 *
 * WAS DARF, ENTSCHEIDET NICHT DIE ADRESSE. Steht dort ein Bildschirm, den
 * dieser Zugang nicht hat, prüft `App` gegen das Menü und fällt auf
 * „Aufträge" zurück; der Server verlangt seine Rechte ohnehin selbst.
 */

export interface Stelle {
  wo: string; akte: string | null; reiter: string | null; kennung?: string | null;
  /** DER BEREICH (.333): ne4 steht vorn in der Adresse (#/ne4/...), NE3 ohne Vorsilbe. */
  bereich?: string | null;
  /** .597: das offene Haus bzw. der offene Eintrag einer Liste (#/ne4/ne4_auftraege/<id>). */
  offen?: string | null;
}

const KENNUNG = /^[0-9a-f-]{36}$/i;
const WORT = /^[a-z][a-z0-9_]{1,40}$/;
/* Ein Bereich heisst ne3, ne4, ... (Tabelle `bereich`). NE3 ist der Bestand und
   steht nicht in der Adresse, damit jede alte Adresse weiter gilt. */
const BEREICH = /^ne[0-9]$/;

export function ausAdresse(hash = typeof window !== 'undefined' ? window.location.hash : ''): Stelle {
  let teile = String(hash ?? '').replace(/^#\/?/, '').split('/').filter(Boolean);
  let bereich: string | null = null;
  if (teile[0] && BEREICH.test(teile[0])) { bereich = teile[0]; teile = teile.slice(1); }
  /* DER QUICKLINK (.332): #/a/<KLS-ID>, auch SM-Nummer oder MDS-Nummer. Marvin,
     07.10.2026: „ich will gerne den auftrag als quicklink haben in unser
     system." Aufgelöst wird er in App, nach der Anmeldung, über den Server.
     Seit .333 auch #/a/ne4/<Kennung>: der Bereich steht dann in der Mitte. */
  if (teile[0] === 'a' && teile[1] && BEREICH.test(teile[1])) { bereich = teile[1]; teile = [teile[0], ...teile.slice(2)]; }
  if (teile[0] === 'a' && /^[A-Za-z0-9-]{1,60}$/.test(decodeURIComponent(teile[1] ?? ''))) {
    return { wo: 'auftraege', akte: null, reiter: null, kennung: decodeURIComponent(teile[1]!), bereich };
  }
  if (teile[0] === 'akte' && KENNUNG.test(teile[1] ?? '')) {
    return { wo: 'akte', akte: teile[1], reiter: WORT.test(teile[2] ?? '') ? teile[2] : null, bereich };
  }
  if (teile[0] && WORT.test(teile[0]) && teile[0] !== 'akte') {
    return { wo: teile[0], akte: null, reiter: null, bereich, offen: KENNUNG.test(teile[1] ?? '') ? teile[1] : null };
  }
  /* .597: ohne Bildschirm in der Adresse die Aufträge des Bereichs, keine Übersicht mehr. */
  return { wo: bereich && bereich !== 'ne3' ? `${bereich}_auftraege` : 'auftraege', akte: null, reiter: null, bereich };
}

/** Der Quicklink eines Auftrags, zum Kopieren: über die KLS-ID, sonst die Kennung. */
export function quicklink(a: { kls_id?: string | null; id: string }, haus = typeof window !== 'undefined' ? window.location.origin : '', bereich?: string | null): string {
  const mitte = bereich && bereich !== 'ne3' ? `${bereich}/` : '';
  return `${haus}/#/a/${mitte}${encodeURIComponent(String(a.kls_id ?? a.id))}`;
}

export function adresseFuer(s: Stelle): string {
  const vorn = s.bereich && s.bereich !== 'ne3' ? `#/${s.bereich}/` : '#/';
  return s.wo === 'akte' && s.akte
    ? `${vorn}akte/${s.akte}${s.reiter ? '/' + s.reiter : ''}`
    : `${vorn}${s.wo}${s.offen ? '/' + s.offen : ''}`;
}

export function inAdresse(s: Stelle): void {
  if (typeof window === 'undefined') return;
  /* .597: Steht hinter dem Bildschirm ein offenes Haus und bleibt der Bildschirm derselbe, bleibt es stehen. */
  const jetzt = ausAdresse();
  const offen = s.offen !== undefined ? s.offen : (s.wo !== 'akte' && jetzt.wo === s.wo ? jetzt.offen ?? null : null);
  const ziel = adresseFuer({ ...s, offen });
  if (window.location.hash === ziel) return;
  try {
    window.history.replaceState(window.history.state, '',
      window.location.pathname + window.location.search + ziel);
  } catch { /* ein Browser, der das verweigert, verliert nur den Platz beim Neuladen */ }
}

/**
 * .593: DEN AUFTRAG ALS MAIL WEITERGEBEN (NE3 und NE4).
 * Marvin, 02.11.2026: „du klickst auf E-Mail dort oben, dann öffnet sich dein Outlook und dann ist
 * schon im Betreff der komplette Auftrag, also die Adresse mit KLS-ID, steht dann drinne. Du wählst
 * nur noch die Empfänger aus und schreibst deinen Text."
 * Kein Empfänger, kein Text: das Mailprogramm des Rechners öffnet sich, sonst nichts.
 */
export function auftragsBetreff(a: { kls_id?: string | null; strasse?: string | null; hausnummer?: string | null; hausnummer_zusatz?: string | null; plz?: string | null; ort?: string | null; sm?: string | null }): string {
  /* .595: alles mit Komma und Leerzeichen getrennt (Marvin: „KLS, das trennen wir aber nicht mit einem Punkt"). */
  return [`${a.strasse ?? ''} ${a.hausnummer ?? ''}${a.hausnummer_zusatz ?? ''}`.trim(), `${a.plz ?? ''} ${a.ort ?? ''}`.trim(),
          a.kls_id ? `KLS-ID ${a.kls_id}` : null, a.sm ? `SM ${a.sm}` : null].filter(Boolean).join(', ');
}
export function auftragsMail(a: Parameters<typeof auftragsBetreff>[0]): string {
  return `mailto:?subject=${encodeURIComponent(auftragsBetreff(a))}`;
}

/**
 * .597: NACH DEM NEULADEN DORT WEITER, WO MAN WAR.
 * Marvin, 02.11.2026: „wenn wir ein Update haben und ein Update einspielen, dann bitte danach die
 * gleiche Maske wieder laden, wo wir aufgehört haben. Ja, selbst wenn du das Bild angezeigt bekommst
 * mit dem Wartungsmodus und klickst dann da auf Neu laden."
 * Der Bildschirm stand schon in der Adresse, das offene Haus einer Liste nicht. Jetzt steht es dahinter
 * (#/ne4/ne4_auftraege/<id>); die Seite liest es beim Aufbau und schreibt es bei jedem Öffnen und Schließen.
 */
export function offenAusAdresse(wo: string): string | null {
  const s = ausAdresse();
  return s.wo === wo ? s.offen ?? null : null;
}
export function offenInAdresse(wo: string, id: string | null): void {
  if (typeof window === 'undefined') return;
  const s = ausAdresse();
  if (s.wo !== wo) return;
  inAdresse({ ...s, offen: id });
}
